import locales from '@nike/web-shell-locale-data/data/locales.json';
import get from 'lodash.get';
import { getTraceId } from 'nav-utilities';
import {
  offPlatformCountries,
  URL,
  IMAGE_BASE_URL,
  SIZING_QUERY_V2,
  LANDING_PAGE,
  EDITORIAL,
  SEO_REDIRECT_URL,
  PDP,
  GRID_WALL,
  PDP_GRID_WALL,
  SOURCE_ANALYSIS,
  SITE_HOST_NIKE,
  SIZECHART,
} from '../constants';
import webShellLocMappings from '../data/webShellLocMappings.json';

export async function fetchGlobalConfig(country) {
  try {
    const url = getUrl('global_config', { country });
    const { config } = await fetch(url).then(res => res.json());
    return config;
  } catch (err) {
    window.NikeDotcomNav?.newrelic.error(err, 'fetchGlobalConfig');
    return {};
  }
}

/**
 * Determine the marketplace to use based on the global config useMarketplaceId
 * @param {string} mismatchCountryCode
 * @param {import('@nike/web-shell-types').LocaleData} locale
 * @returns {Promise<string>} marketplace
 */
export async function determineMarketplace(mismatchCountryCode, locale) {
  const marketplaceId = locale?.marketplaceId;
  if (marketplaceId) {
    const { useMarketplaceId } = await fetchGlobalConfig(mismatchCountryCode);
    return useMarketplaceId ? marketplaceId : mismatchCountryCode;
  }
  return mismatchCountryCode;
}

/**
 * A helper function for generating a nike.com cloud href
 * @param {import('./hooks/localeHook').Locale} locale from app's initial data fields
 * @returns {String}
 */
export function getCloudBaseUrl({ urlParam, country, cloudUrlFragment }) {
  const baseUrl = `https://${process.env.HOST_NAME}`;
  if (country?.toUpperCase() === 'CN') {
    return `https://www.nike.com.cn`;
  }
  const isDefaultUS = country?.toUpperCase() === 'US' && urlParam === 'en';
  if (isDefaultUS) return baseUrl;

  const cloudUrl = `${baseUrl}/${cloudUrlFragment && cloudUrlFragment + '/'}`;
  // when country is not passed, we use the country from the cloudUrlFragment
  // this happens when selecting off platform countries from geo mismatch modal
  const countryFromCloudFragment = cloudUrlFragment?.split('/')?.[0]?.toUpperCase();
  if (offPlatformCountries.includes(country?.toUpperCase() || countryFromCloudFragment)) {
    const offPlatformCountry = country || countryFromCloudFragment;
    return (
      URL[`${offPlatformCountry}_${urlParam.split('-')[0]}_HOMEPAGE`?.toUpperCase()] ||
      URL[`${offPlatformCountry}_HOMEPAGE`?.toUpperCase()]
    );
  }

  return cloudUrl;
}

/**
 * Gets localized url for a specified page (i.e. Favorites, Orders)
 * @param page {String} page to generate url for
 * @returns {function(string): string}
 */
export function getLocalizedUrl(page) {
  /**
   * Generates localized url
   * @param {import('./hooks/localeHook').Locale} locale from app's initial data fields
   * @returns {function(string): string}
   */
  const localizeUrl = locale => {
    const baseUrl = getCloudBaseUrl(locale);
    return baseUrl.endsWith('/') ? `${baseUrl}${page}` : `${baseUrl}/${page}`;
  };
  return localizeUrl;
}

/**
 * Generates suggestion URL for typeahead suggestions and search query urls
 * @param props {Object}
 * @param props.locale {import('./hooks/localeHook').Locale} locale data for the page
 * @param props.searchTerm {String} searchTerm becomes vst
 * @param props.query {string} q query param value
 * @param props.path {string} url path part
 * @returns {String}
 */
export function getSearchUrl({ locale, searchTerm, query, path }) {
  const { country, urlParam, cloudUrlFragment } = locale;
  let baseUrl = getCloudBaseUrl({ country, urlParam, cloudUrlFragment });
  const params = [];
  if (query) {
    params.push(`q=${encodeURIComponent(query)}`);
  }
  params.push(`vst=${encodeURIComponent(searchTerm)}`);

  baseUrl += baseUrl.endsWith('/') ? '' : '/';
  return `${baseUrl}${path}?${params.join('&')}`;
}

/**
 * Generates the image url for visual search.
 * Image will typically be portrait unless the product is a Nike By You, aka NikeId, product
 * then the image will be squarish
 * @param {{
 * styleNumber: string;
 * colorCode: string;
 * imageUrls?: {portraitUrl: string; squarishUrl: string};
 * nikeId: boolean;
 * }} product product object from VS api response
 * @returns {string}
 */
export const getProductImg = product => {
  const { colorwayImages, copy, productCode: colorCode, featuredAttributes } = product;
  const { title: styleNumber } = copy;
  const nikeId = featuredAttributes?.includes('CUSTOMIZED');

  // Immediately return portraitUrl if exists
  if (colorwayImages?.portraitURL) {
    return colorwayImages.portraitURL;
  }
  // Old method of crafting a url still works for
  // non nikeId products, fall back to this if no portraitUrl
  if (!nikeId) {
    return `${IMAGE_BASE_URL}/${styleNumber}_${colorCode}/${encodeURI(SIZING_QUERY_V2)}`;
  }

  if (colorwayImages) {
    return colorwayImages.squarishURL;
  }
  // Fallback to this if no imageUrls are provided,
  // returns an 'Image not found' jpg if product is not found
  return `${IMAGE_BASE_URL}/${styleNumber}_${colorCode}/${encodeURI(SIZING_QUERY_V2)}`;
};

/**
 * Returns response from SEO fetch
 * @param url {String}
 * @param body {Object}
 * @return {Promise}
 */
export async function fetchSeo(url, body) {
  const traceId = getTraceId();
  const spanId = getTraceId();
  let erred = false;
  const res = await Promise.race([
    fetch(url, {
      method: 'POST',
      headers: {
        'nike-api-caller-id': 'nike:dotcom.global-nav:service:1.0',
        'X-B3-TraceId': traceId,
        'X-B3-SpanId': spanId,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        ...body,
      }),
    }),
    new Promise((_, reject) => setTimeout(() => reject(new Error('SEO fetch timeout')), 1000)),
  ]);
  erred = res.status < 200 || res.status >= 300;
  const responseJson = await res.json();
  if (erred) {
    const hasErrorMessage = responseJson?.errors?.length > 0;
    throw new Error(
      `${responseJson?.message}: ${hasErrorMessage ? responseJson?.errors[0]?.message : ''}`,
    );
  }
  return responseJson;
}

/**
 * Returns response from SEO about landing pages.
 * @param seoPayload {Object}
 * @param newrelic {Object}
 * @param traceContext {String}
 * @param queryParams {String}
 * @return {Promise<string>}
 */
async function getPageUrl(seoPayload, newrelic, traceContext, queryParams) {
  const stopTrace = newrelic.startTrace(traceContext);
  // Lookup the URL using SEO analysis service
  const urlAnalysis = await fetchSeo(URL.API_SEO_ANALYSIS_V2, { url: window.location.href });
  // extend result and incoming seo payload.
  const analysisParts = get(urlAnalysis, SOURCE_ANALYSIS);
  const { consumerChannel: consumerChannelId, pageId, urlType } = analysisParts;
  const redirectData = await fetchSeo(URL.API_SEO_GENESIS_V2, {
    consumerChannelId,
    pageId,
    urlType,
    ...seoPayload,
  });
  stopTrace();
  // The URL may contain a change to the host name, like for China
  if (redirectData.url) {
    return `${redirectData.url}${queryParams}`;
  }
  throw new Error('No pageUrl in response');
}

/**
 * Returns appropriate redirect url
 * @param {{
 * pageType: string, conceptIds: Array<string> , viewChannel: string;}} appInfo - appInfo from dotcomNavInstance
 * @param {string} mismatchCountryCode - country code for the country selected in geo mismatch modal like 'fr'
 * @param {import('./locale').Language} languageInfo - for the country selected in geo mismatch modal
 * @param {Object} newrelic
 * @param {import('./hooks/localeHook').Locale} oldLocale - for the country the page is currently for
 * @return {Promise<string>}
 */
export async function getRedirectUrl(
  appInfo,
  mismatchCountryCode,
  languageInfo,
  newrelic,
  oldLocale,
) {
  const currentUrl = window.location.href;
  /*
  breaks up url for example 'https://www.nike.com/ca/fr/experience' to ['https://www.nike.com/', 'experience']
  when we don't have a cloudUrlFragment, 'www.nike.com/' works to split the url ['https://', 'experience']
  */
  const splitUrl = currentUrl.split((oldLocale.cloudUrlFragment || SITE_HOST_NIKE) + '/');
  const newLocale = webShellLocMappings[mismatchCountryCode.toLowerCase()]?.find(
    country => country.urlParam === languageInfo.urlParam,
  );
  const marketplace = await determineMarketplace(mismatchCountryCode, newLocale);

  // get the country from the cloudUrlFragment as webshell mapping does not have country details
  const countryFromCloudFragment = newLocale?.cloudUrlFragment?.split('/')?.[0]?.toUpperCase();
  const isOffPlatformCountry = offPlatformCountries.includes(countryFromCloudFragment);

  // if the url is not two values long something went wrong, like a dev route, and we default to the homepage
  const pathSansCloudUrlFragment =
    (splitUrl.length === 2) & !isOffPlatformCountry ? splitUrl[1] : '';
  const getUrlToUse = getLocalizedUrl(pathSansCloudUrlFragment);
  const urlToUse = getUrlToUse({ ...newLocale, country: mismatchCountryCode });

  // If the country is an off platform country, then we just return the landing page for that country
  if (isOffPlatformCountry) {
    return urlToUse;
  }
  const queryParams = window.location.search || '';
  let seoPayload = {
    languageTag: newLocale.language.toLowerCase(),
    marketplace,
  };
  const { pageType, conceptIds, viewChannel } = appInfo;
  // Use viewChannel to determine if we have an LP or article redirect. If so, handle the 1-off.
  if ([LANDING_PAGE, EDITORIAL, SIZECHART].includes(viewChannel)) {
    try {
      return await getPageUrl(
        seoPayload,
        newrelic,
        `${SEO_REDIRECT_URL}:${viewChannel}`,
        queryParams,
      );
    } catch (err) {
      newrelic.error(err, `${SEO_REDIRECT_URL}:${viewChannel}`);
      return urlToUse;
    }
  }

  // Not a viewChannel that requires specific handling, process normally by pageType
  // Appears to only be used for handling PDP & GRIDWALL.
  switch (pageType) {
    case PDP.toUpperCase(): {
      const { styleColor } = appInfo;
      if (!styleColor) {
        newrelic.error(new Error('Missing styleColor'), `${SEO_REDIRECT_URL}:${PDP}`);
        return urlToUse;
      }
      seoPayload = { ...seoPayload, pageType, styleColor };
      break;
    }
    case GRID_WALL.toUpperCase():
      if (!conceptIds || conceptIds.length < 1) {
        newrelic.error(new Error('Missing conceptIds'), `${SEO_REDIRECT_URL}:${GRID_WALL}`);
        return urlToUse;
      }
      seoPayload = { ...seoPayload, conceptIds, pageType };
      break;
    default:
      newrelic.log(
        { ...appInfo, redirectUrl: urlToUse, currentUrl, message: 'Fallback redirect logic used' },
        SEO_REDIRECT_URL,
      );
      return urlToUse;
  }

  try {
    return await getPageUrl(
      seoPayload,
      newrelic,
      `${SEO_REDIRECT_URL}:${PDP_GRID_WALL}`,
      queryParams,
    );
  } catch (err) {
    newrelic.error(err, `${SEO_REDIRECT_URL}:${PDP_GRID_WALL}`);
    return urlToUse;
  }
}

/**
 * Map of special membership urls for use in the MembershipUrl function.
 * Exported for testing.
 */
export const specialMembershipLocales = {
  TR: {
    country: 'TR',
    value: `https://${process.env.HOST_NAME}/tr/uyelik`,
  },
  'HU-HU': {
    country: 'HU',
    language: 'hu',
    value: `https://${process.env.HOST_NAME}/hu/tagsag`,
  },
  'PT-PT': {
    country: 'PT',
    language: 'pt',
    value: `https://${process.env.HOST_NAME}/pt/subscricao`,
  },
  TH: {
    country: 'TH',
    value: `https://${process.env.HOST_NAME}/th/smachik`,
  },
  RU: {
    country: 'RU',
    value: `https://${process.env.HOST_NAME}/ru/registratsiya`,
  },
  GR: {
    country: 'GR',
    value: `https://${process.env.HOST_NAME}/gr/melos`,
  },
  'SE-SV': {
    country: 'SE',
    language: 'sv',
    value: `https://${process.env.HOST_NAME}/se/medlemskap`,
  },
  'NO-NO': {
    country: 'NO',
    language: 'no',
    value: `https://${process.env.HOST_NAME}/no/medlemskap`,
  },
  DK: {
    country: 'DK',
    value: `https://${process.env.HOST_NAME}/dk/medlemskab`,
  },
  'ES-CA': {
    country: 'ES',
    language: 'ca',
    value: `https://${process.env.HOST_NAME}/es/ca/membership`,
  },
  ES: {
    country: 'ES',
    value: `https://${process.env.HOST_NAME}/es/inscripcion`,
  },
  PL: {
    country: 'PL',
    value: `https://${process.env.HOST_NAME}/pl/czlonkostwo`,
  },
  CZ: {
    country: 'CZ',
    value: `https://${process.env.HOST_NAME}/cz/clenstvi`,
  },
  FR: {
    country: 'FR',
    value: `https://${process.env.HOST_NAME}/fr/adhesion`,
  },
  'CH-FR': {
    country: 'CH',
    language: 'fr',
    value: `https://${process.env.HOST_NAME}/ch/fr/adhesion`,
  },
  'CA-FR': {
    country: 'CA',
    language: 'fr',
    value: `https://${process.env.HOST_NAME}/ca/fr/adhesion`,
  },
  'BE-FR': {
    country: 'BE',
    language: 'fr',
    value: `https://${process.env.HOST_NAME}/be/fr/adhesion`,
  },
  PR: {
    country: 'PR',
    value: `https://${process.env.HOST_NAME}/pr/membresia`,
  },
  MX: {
    country: 'MX',
    value: `https://${process.env.HOST_NAME}/mx/membresia`,
  },
  'MA-FR': {
    country: 'MA',
    language: 'fr',
    value: `https://${process.env.HOST_NAME}/ma/adhesion`,
  },
  'LU-FR': {
    country: 'LU',
    language: 'fr',
    value: `https://${process.env.HOST_NAME}/lu/adhesion`,
  },
  CL: {
    country: 'CL',
    value: `https://${process.env.HOST_NAME}/cl/membresia`,
  },
  AR: {
    country: 'AR',
    value: `https://${process.env.HOST_NAME}/ar/membresia`,
  },
  'US-ES': {
    country: 'US',
    language: 'es',
    value: `https://${process.env.HOST_NAME}/us/es/membresia`,
  },
  UY: {
    country: 'UY',
    value: `https://${process.env.HOST_NAME}/uy/membresia`,
  },
  XL: {
    country: 'XL',
    value: `https://${process.env.HOST_NAME}/xl/membresia`,
  },
};

/** MembershipUrl creates the membership url based on locale (it's also a rewrite of the membership flag in V2. V3 flags are written in json, which doesn't allow
 * javascript methods)
 * @param {{
 *  urlParam: String;
 *  country: String;
 * }} locale object containing data about the current region
 * @returns {string}
 */
export const MembershipUrl = locale => {
  const { urlParam, country } = locale;

  // Uses country and urlParam from locale to search the specialLocalesObject to determine
  // whether we are in a special region or not. If we are, use the selection to return the value.
  // If we aren't in a special region, the getLocalizedUrl method to construct the url.
  const location =
    specialMembershipLocales[`${country?.toUpperCase()}-${urlParam?.toUpperCase()}`] ||
    specialMembershipLocales[`${country?.toUpperCase()}`];

  return location?.value || getLocalizedUrl('membership')(locale);
};

/**
 * Returns the langRegion for this country-urlParam combo
 *
 * @param country {String}
 * @param urlParam {Object}
 * @returns {Object}
 */
export function getLangRegion(country, urlParam) {
  const usDefaultLangRegion = 'en-US';
  if (!country) return usDefaultLangRegion;
  const locale = locales.find(
    locale => country.toLowerCase() === locale.country && urlParam === locale.urlParam,
  );
  if (!locale) {
    return usDefaultLangRegion;
  }

  return locale.langRegion;
}

/**
 * Returns properly formatted url lang/region
 * @param lang {String}
 * @returns {String}
 */
export function langRegionConvert(lang) {
  const splits = lang.split('-');
  return `${splits[0]}_${splits[1].toLowerCase()}`;
}

/**
 * Render a URL string template given the url name and params.
 * @param name {String}
 * @param params {Object}
 * @param convert {Boolean} convert lang params to legacy
 * @returns {String}
 */
export default function getUrl(name, params = {}, convert = true) {
  const template = URL[name.toUpperCase()];
  if (!template) {
    throw new Error(`Url not registered: ${name}`);
  }

  return Object.keys(params).reduce((string, key) => {
    let param = (params[key] ? params[key] : '').toString();
    switch (key) {
      case 'language':
      case 'locale':
        if (convert) {
          param = langRegionConvert(getLangRegion(params.country, param));
        }
        break;
      case 'country':
      default:
        param = param.toLowerCase();
        break;
    }
    return string.replace(new RegExp(`:${key}`, 'g'), param);
  }, template);
}
